import React, { useCallback, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import NewRequestPicker from './NewRequestPicker';
import Tooltip from './Tooltip';

/**
 * The tab strip's "+" where the strip holds request tabs: it opens the picker — HTTP,
 * GraphQL, WebSocket, Server-Sent Events, gRPC (`NewRequestPicker`). The chevron after it is
 * the list of open tabs (EnhancedTabManager).
 *
 * One click more than before for a plain HTTP request from the mouse — Ctrl+T and the
 * tab context menu's "New Tab" still open one directly.
 *
 * `onPick(optionId)` — an id from `REQUEST_KIND_OPTIONS`; `blankRequestOfKind` knows them.
 */
export default function NewRequestKindMenu({ onPick }) {
  const { t } = useTranslation();
  const buttonRef = useRef(null);
  const [anchor, setAnchor] = useState(null);
  // closed from the keyboard (Escape / Tab): focus goes back to where it came from
  const close = useCallback((how) => {
    setAnchor(null);
    if (how?.returnFocus) buttonRef.current?.focus();
  }, []);

  const toggle = () => {
    if (anchor) { setAnchor(null); return; }
    const rect = buttonRef.current?.getBoundingClientRect();
    // `x` is the button's RIGHT edge: the menu grows to the left from there, like the all-tabs
    // list beside it (both sit at the right end of the strip).
    if (rect) setAnchor({ x: rect.right, y: rect.bottom + 8 });
  };

  return (
    <>
      <Tooltip content={t('tabs.newRequestTab')} side="bottom" disabled={!!anchor}>
        <button
          ref={buttonRef}
          type="button"
          aria-haspopup="menu"
          aria-expanded={!!anchor}
          onClick={toggle}
          className="ml-2 py-1 rounded-lg bg-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-200 dark:hover:text-zinc-200 dark:hover:bg-zinc-700 transition-colors"
        >
          <Plus size={11} />
        </button>
      </Tooltip>
      {anchor && (
        <NewRequestPicker
          x={anchor.x}
          y={anchor.y}
          ignoreRef={buttonRef}
          onClose={close}
          onPick={(optionId) => { setAnchor(null); onPick(optionId); }}
        />
      )}
    </>
  );
}
